import { isInAppPath } from "@/shared/appLink.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { Mail } from "lucide-react";
import type { JSX } from "react";
import { Link } from "wouter";

type EmailLinkComponentData = Extract<ArtifactComponent, { type: "emailLink" }>;

type EmailSubjectLinkProps = {
  href?: string;
  subject: string;
};

/**
 * Routes canonical app paths through wouter and external HTTP(S) URLs through
 * native browser navigation. Unsupported URL shapes remain plain text.
 */
function EmailSubjectLink({ href, subject }: EmailSubjectLinkProps): JSX.Element {
  if (!href) return <span>{subject}</span>;
  if (isInAppPath(href)) {
    return (
      <Link href={href} className="link">
        {subject}
      </Link>
    );
  }
  // Only linkify http(s) URLs. `href` is LLM-authored artifact data, so a
  // `javascript:`/`data:` scheme could execute on click — fall back to plain
  // text for those. `URL.parse` returns null for an unparseable value, which
  // likewise stays text; it matches the guard `FieldRowBlock` uses.
  const parsed = URL.parse(href);
  if (!parsed || !/^https?:$/.test(parsed.protocol)) return <span>{subject}</span>;
  return (
    // Wouter routes through the SPA History API, so external URLs use native navigation.
    <a href={href} target="_blank" rel="noopener noreferrer" className="link">
      {subject}
    </a>
  );
}

/**
 * Compact affected-email row: mail icon + linked subject + muted "from
 * <sender>". Used by the email-change card. Canonical app paths let enclosing
 * navigation boundaries choose an in-place surface, with the page as fallback;
 * external email URLs open in a new tab.
 */
export const EmailLinkBlock = ({ component }: { component: EmailLinkComponentData }) => {
  const subject = component.subject || "No subject";
  return (
    <div className="flex flex-row items-center gap-2 text-sm">
      <Mail className="size-4 shrink-0 text-muted-foreground" />
      <EmailSubjectLink href={component.href} subject={subject} />
      {component.sender ? (
        <span className="text-muted-foreground">from {component.sender}</span>
      ) : null}
    </div>
  );
};
